import { useEffect, useRef, useState } from 'react';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { useConfig } from '@config/provider';
import { resolveBasePath } from '@app/utils/api';

type Option = { id: string; name?: string; displayName?: string; hostname?: string; mgmtAddress?: string };
const label = (item: Option) => item.displayName || item.hostname || item.name || item.mgmtAddress || item.id;

/** Searchable selectors page independently of the table and retain the selected ID. */
export default function PagedResourceSelect({ resource, value, onChange, allValue = '', allLabel, siteId, deviceRole }: {
  resource: 'sites' | 'devices'; value: string; onChange: (id: string) => void;
  allValue?: string; allLabel: string; siteId?: string; deviceRole?: string;
}) {
  const orgId = useActiveOrgId();
  const { apiBaseUrl } = useConfig();
  const basePath = resolveBasePath(apiBaseUrl);
  const { fetchOrgResource } = useAuthFetch();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const scopeKey = JSON.stringify([orgId, resource, siteId, deviceRole]);
  const [cursor, setCursor] = useState<{ scope: string; value: string }>();
  const after = cursor?.scope === scopeKey ? cursor.value : undefined;
  const [next, setNext] = useState<string>();
  const [items, setItems] = useState<Option[]>([]);
  const [selected, setSelected] = useState<Option>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef(0);
  useEffect(() => {
    const timer = setTimeout(() => { setQuery(search.trim()); setCursor(undefined); }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => { setCursor(undefined); setItems([]); setNext(undefined); setSelected(undefined); }, [orgId, resource, siteId, deviceRole]);
  useEffect(() => {
    if (!orgId) { setLoading(false); return; }
    const id = ++requestId.current;
    setLoading(true); setError('');
    const params = new URLSearchParams({ limit: '50' });
    if (query) params.set('q', query);
    if (after) params.set('after', after);
    if (siteId) params.set('siteId', siteId);
    if (resource === 'devices' && deviceRole) params.set('role', deviceRole);
    if (resource === 'devices') params.set('includeRunningConfigStatus', 'false');
    void fetchOrgResource<{ items: Option[]; meta?: { next?: string } }>(basePath, orgId, `${resource}?${params}`).then((page) => {
      if (id !== requestId.current) return;
      setItems((previous) => [...new Map([...(after ? previous : []), ...page.items].map((item) => [item.id, item])).values()]);
      setNext(page.meta?.next || undefined);
    }).catch((caught) => { if (id === requestId.current) setError(caught instanceof Error ? caught.message : 'Unable to load options'); })
      .finally(() => { if (id === requestId.current) setLoading(false); });
    return () => { requestId.current += 1; };
  }, [orgId, resource, siteId, deviceRole, query, after, basePath, fetchOrgResource]);
  useEffect(() => { const item = items.find((item) => item.id === value); if (item) setSelected(item); }, [items, value]);
  return <div>
    <input className="input" aria-label={`Search ${resource}`} placeholder={`Search ${resource}`} value={search} onChange={(event) => setSearch(event.target.value)} />
    <select className="select" aria-label={allLabel} value={value} onChange={(event) => onChange(event.target.value)}>
      <option value={allValue}>{allLabel}</option>
      {value !== allValue && !items.some((item) => item.id === value) && <option value={value}>{selected?.id === value ? label(selected) : value}</option>}
      {items.map((item) => <option key={item.id} value={item.id}>{label(item)}</option>)}
    </select>
    {next && <button type="button" className="ui-button ui-button--ghost" disabled={loading} onClick={() => setCursor({ scope: scopeKey, value: next })}>Load more {resource}</button>}
    {loading && <span role="status">Loading…</span>}
    {error && <span role="alert">{error}</span>}
  </div>;
}
